import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/* Module-level constants — stable references prevent unnecessary re-renders
   of child motion elements when parent re-renders. */
const MOTION_EASE = [0.22, 0.8, 0.36, 1];
const MOTION_VIEWPORT_BASE = { amount: 0.18 };

/**
 * Reveal — subtle scroll-triggered fade+rise.
 * Respects prefers-reduced-motion.
 */
export function Reveal({
    children,
    delay = 0,
    y = 10,
    duration = 0.45,
    once = true,
    className = "",
    as: As = "div",
}) {
    const reduced = useReducedMotion();
    // Memoize the per-render motion props so they keep stable identity when
    // none of the inputs (y, duration, delay, once) change.
    const initial = React.useMemo(() => ({ opacity: 0, y }), [y]);
    const whileInView = React.useMemo(() => ({ opacity: 1, y: 0 }), []);
    const viewport = React.useMemo(() => ({ ...MOTION_VIEWPORT_BASE, once }), [once]);
    const transition = React.useMemo(
        () => ({ duration, delay, ease: MOTION_EASE }),
        [duration, delay],
    );

    if (reduced) {
        return <As className={className}>{children}</As>;
    }
    return (
        <motion.div
            initial={initial}
            whileInView={whileInView}
            viewport={viewport}
            transition={transition}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export default Reveal;
